import type { Toast as ToastType } from "../../stores/transactionStore";

// --- Props ---

interface ToastProps {
	toast: ToastType;
	onDismiss: (id: string) => void;
}

// --- Constants ---

const BASE_SCAN_URL = "https://sepolia.basescan.org/tx";

// --- Helpers ---

function truncateHash(hash: string): string {
	return `${hash.slice(0, 6)}...${hash.slice(-4)}`;
}

function StatusIcon({ type }: { type: ToastType["type"] }) {
	if (type === "pending") {
		return <div className="toast__spinner" />;
	}

	if (type === "success") {
		return (
			<svg
				className="toast__icon"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
			>
				<path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
			</svg>
		);
	}

	return (
		<svg
			className="toast__icon"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
		>
			<path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

// --- Component ---

export function Toast({ toast, onDismiss }: ToastProps) {
	const { id, type, title, message, hash } = toast;

	return (
		<div className={`toast toast--${type}`}>
			<div className="toast__bar" />

			<div className="toast__content">
				<div className="toast__status">
					<StatusIcon type={type} />
				</div>

				<div className="toast__body">
					<div className="toast__title">{title}</div>
					<div className="toast__message">{message}</div>
					{hash && (
						<a
							className="toast__link"
							href={`${BASE_SCAN_URL}/${hash}`}
							target="_blank"
							rel="noopener noreferrer"
						>
							{truncateHash(hash)} ↗
						</a>
					)}
				</div>

				<button
					className="toast__dismiss"
					onClick={() => onDismiss(id)}
					aria-label="Dismiss notification"
				>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</button>
			</div>
		</div>
	);
}
